// upload-panel.component.ts
import { Component, OnInit, ViewChild, OnDestroy, ElementRef } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { UploadService, UploadFile, ReconfirmationRequest } from '../../services/upload/upload.service';
import { CommonModule } from '@angular/common';
import { FloatingPanelComponent } from '../floating-panel/floating-panel.component';
import { DropzoneDirective } from '../dropzone/dropzone.directive';
import { environment } from '../../../environments/environment';
import { ModalDialogComponent } from '../modal-dialog/modal-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MessageModalComponent } from '../message-modal/message-modal.component';
import { response } from 'express';

@Component({
  selector: 'app-upload-panel',
  standalone: true,
  imports: [CommonModule, FloatingPanelComponent], // ✅ Import this
  templateUrl: './upload-panel.component.html',
  styleUrls: ['./upload-panel.component.css'],
})
export class UploadPanelComponent implements OnInit, OnDestroy {
  @ViewChild(DropzoneDirective) dropzoneDirective?: DropzoneDirective;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  uploads$: Observable<UploadFile[]>;
  totalProgress$: Observable<number>;
  activeUploadsCount$: Observable<number>;
  completedUploadsCount$: Observable<number>;

  isPanelVisible = false;
  isPanelMinimized = false;
  activeCount = 0;
  private subscriptions: Subscription = new Subscription();

  reconfirmationRequests$: Observable<ReconfirmationRequest[]>;
  private currentRequestId: string | null = null;

  constructor(
    private uploadService: UploadService,
    private dialog: MatDialog
  ) {
    this.uploads$ = this.uploadService.uploads$;
    this.totalProgress$ = this.uploadService.totalProgress$;
    this.activeUploadsCount$ = this.uploadService.activeUploadsCount$;
    this.completedUploadsCount$ = this.uploadService.completedUploadsCount$;
    this.reconfirmationRequests$ = this.uploadService.reconfirmationNeeded$;
  }

  ngOnInit(): void {
    // Show panel when uploads start
    this.activeUploadsCount$.subscribe((count) => {
      if (count > 0) {
        this.isPanelVisible = true;
      }
      this.activeCount = count;
    });

    this.subscriptions.add(
      this.uploadService.closePanelOnLogout$.subscribe((shouldClose) => {
        if (shouldClose) {
          this.isPanelVisible = false;
          this.uploadService.resetClosePanelOnLogoutSignal();
        }
      })
    );

    this.subscriptions.add(
      this.reconfirmationRequests$.subscribe((requests) => {
        if (requests.length > 0) {
          this.isPanelVisible = true;
        }
      })
    );
  }

  onPanelMinimized(minimized: boolean): void {
    this.isPanelMinimized = minimized;
  }

  cancelUpload(id: string, fileName: string, modalInstance: ModalDialogComponent): void {
    modalInstance.dialogRef.close();
    this.uploadService.cancelUpload(id).subscribe(
      (response) => {
        this.messageDialog('Success', `${fileName} uploading Canceled`);
      },
      (error) => {
        this.messageDialog('Error', 'Error in  cancel upload');
        console.error('Cancel error:', error);
      }
    );
  }

  cancelAllUploads(modalInstance: ModalDialogComponent): void {
    // Cancel all uploads
    modalInstance.dialogRef.close();
    this.uploadService.cancelAllUploads().subscribe((results) => {
      this.clearCompleted();
      this.messageDialog('Success', 'Canceled all upload');
      if (this.dropzoneDirective) {
        this.dropzoneDirective.cancelAll();
      }

      this.isPanelVisible = false;
    });
    // Also reset Dropzone if available
  }

  clearCompleted(): void {
    this.uploadService.removeCompletedUploads();
  }

  formatFileSize(bytes: number): string {
    return this.uploadService.formatFileSize(bytes);
  }

  getStatusClass(status: string): string {
    return status.toLowerCase();
  }

  // Track rows by their stable id so progress updates (which replace each
  // UploadFile with a new object) patch the existing row instead of tearing
  // down and rebuilding its DOM — otherwise the file icon re-fetches and
  // flashes on every chunk, thrashing the main thread.
  trackByUploadId(_index: number, upload: UploadFile): string {
    return upload.id;
  }

  trackByRequestId(_index: number, request: ReconfirmationRequest): string {
    return request.id;
  }

  closeUpload(id: string, fileName: string): void {
    const dialogRef = this.dialog.open(ModalDialogComponent, {
      width: '50rem',
      position: {
        top: '12%',
      },
      data: {
        title: $localize`Cancel upload`,
        description: $localize`Are you sure you want to cancel the upload?`,
        primaryButtonLabel: $localize`Yes, Cancel upload`,
        secondaryButtonLabel: $localize`Cancel`,
        showPrimaryButton: true, // Show primary button
        showSecondaryButton: true, // Secondary button is always shown
      },
    });
    const modalInstance = dialogRef.componentInstance;
    modalInstance.primaryAction.subscribe(() => {
      this.cancelUpload(id, fileName, modalInstance);
    });
  }

  onPanelClosed(): void {
    if (this.activeCount > 0) {
      const dialogRef = this.dialog.open(ModalDialogComponent, {
        width: '50rem',
        data: {
          title: $localize`Cancel all uploads`,
          description: $localize`Are you sure you want to cancel the all file upload?`,
          primaryButtonLabel: $localize`Yes, Cancel upload`,
          secondaryButtonLabel: $localize`Cancel`,
          showPrimaryButton: true, // Show primary button
          showSecondaryButton: true, // Secondary button is always shown
        },
      });
      const modalInstance = dialogRef.componentInstance;
      modalInstance.primaryAction.subscribe(() => {
        this.cancelAllUploads(modalInstance);
      });
    }
    this.isPanelVisible = false;
  }

  messageDialog(title: string, message: string) {
    const dialogRef = this.dialog.open(MessageModalComponent, {
      width: '50rem',
      position: {
        top: '12%',
      },
      data: { title, message },
    });
  }

  /**
   * Called when the user clicks "Resume" on a direct-to-GCS upload that failed mid-session
   * (network drop). The File is still in memory, so we resume in place — no file re-pick.
   */
  resumeFailedUpload(id: string): void {
    this.uploadService.resumeFailedUpload(id);
  }

  /**Called when the user clicks "Resume" on an interrupted upload.**/
  promptForFile(request: ReconfirmationRequest): void {
    this.currentRequestId = request.id;
    this.fileInput.nativeElement.click(); // Opens the file dialog
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0 && this.currentRequestId) {
      const file = input.files[0];
      const requestId = this.currentRequestId;

      try {
        await this.uploadService.resumeWithFile(requestId, file);
      } catch (error: any) {
        this.messageDialog('Error', error.message || 'An unknown error occurred while resuming.');
        console.error(error);
      } finally {
        this.currentRequestId = null;
        input.value = ''; // Reset the input so the user can select the file again.
      }
    }
  }

  discardUpload(request: ReconfirmationRequest): void {
    const dialogRef = this.dialog.open(ModalDialogComponent, {
      width: '50rem',
      position: { top: '12%' },
      data: {
        title: $localize`Discard upload?`,
        description: $localize`Are you sure you want to discard the interrupted upload for <strong>${request.fileName}</strong>? This cannot be undone.`,
        primaryButtonLabel: $localize`Yes, Discard`,
        secondaryButtonLabel: $localize`Cancel`,
        showPrimaryButton: true,
        showSecondaryButton: true,
      },
    });

    const modalInstance = dialogRef.componentInstance;
    modalInstance.primaryAction.subscribe(() => {
      modalInstance.dialogRef.close(); // Close the confirmation dialog

      this.uploadService.discardInterruptedUpload(request.id).subscribe({
        next: () => {
          this.messageDialog('Success', `The upload for ${request.fileName} has been discarded.`);
        },
        error: (err) => {
          this.messageDialog('Error', `An error occurred while discarding the upload.`);
          console.error('Discard error:', err);
        },
      });
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
